// Browser storage (IndexedDB). Everything the app saves lives here; it never writes to the user's folder.
import type { EfModelDocument } from '@modelviewer/core';
import Dexie, { type EntityTable } from 'dexie';

export interface RecentFolder {
  id: string;
  name: string;
  handle: FileSystemDirectoryHandle;
  lastOpened: number;
}

export interface SavedLayout {
  /** `${sourceId}|${setKey}` */
  key: string;
  positions: Record<string, { x: number; y: number }>;
  updated: number;
}

export interface Setting {
  key: string;
  value: unknown;
}

/** Where a sketch started: a model of the folder (a migration or the current model) or another sketch. */
export interface SketchBase {
  setKey: string;
  migrationId: string | null;
  sketchId?: string;
}

/** A design sketch, kept per folder (source). */
export interface SketchRecord {
  id: string;
  sourceId: string;
  /** The migration set it belongs to (for layouts and titles), when known. */
  setKey: string | null;
  base: SketchBase | null;
  doc: EfModelDocument;
  created: number;
  updated: number;
  /** Set for sketches read from `.modelviewer/sketches/` in the folder: read only, never stored here. */
  origin?: { path: string };
}

/** A Markdown note, kept per folder (source). */
export interface NoteRecord {
  id: string;
  sourceId: string;
  title: string;
  body: string;
  created: number;
  updated: number;
  /** Set for notes read from `.modelviewer/notes/` in the folder: read only, never stored here. */
  origin?: { path: string };
}

export const db = new Dexie('modelviewer') as Dexie & {
  folders: EntityTable<RecentFolder, 'id'>;
  layouts: EntityTable<SavedLayout, 'key'>;
  settings: EntityTable<Setting, 'key'>;
  sketches: EntityTable<SketchRecord, 'id'>;
  notes: EntityTable<NoteRecord, 'id'>;
};

db.version(1).stores({
  folders: 'id, lastOpened',
  layouts: 'key',
  settings: 'key',
});

db.version(2).stores({
  sketches: 'id, sourceId, updated',
});

db.version(3).stores({
  notes: 'id, sourceId, updated',
});

/** Adds or refreshes a recently opened folder, reusing the entry for the same directory. */
export async function rememberFolder(handle: FileSystemDirectoryHandle): Promise<RecentFolder> {
  const all = await db.folders.toArray();
  for (const f of all) {
    try {
      if (await f.handle.isSameEntry(handle)) {
        const updated = { ...f, handle, name: handle.name, lastOpened: Date.now() };
        await db.folders.put(updated);
        return updated;
      }
    } catch {
      // A stale handle (folder deleted or moved): ignore it here.
    }
  }
  const folder: RecentFolder = { id: crypto.randomUUID(), name: handle.name, handle, lastOpened: Date.now() };
  await db.folders.put(folder);
  return folder;
}

export async function recentFolders(): Promise<RecentFolder[]> {
  return db.folders.orderBy('lastOpened').reverse().toArray();
}
